import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Nachricht gesendet! Wir melden uns zeitnah bei Ihnen.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Fehler beim Senden. Bitte versuchen Sie es erneut.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="py-20 lg:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Info side */}
          <div>
            <span className="inline-block text-sm font-semibold text-green-600 bg-green-50 px-4 py-1.5 rounded-full mb-4">
              Kontakt
            </span>
            <h2 className="font-['Manrope'] text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
              Wir sind für Sie da
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-10">
              Sie haben Fragen oder möchten mehr über unsere Leistungen erfahren?
              Schreiben Sie uns — wir antworten schnell und unkompliziert.
            </p>

            <div className="space-y-5">
              <div className="flex items-center gap-4" data-testid="contact-phone">
                <div className="h-11 w-11 bg-cyan-50 rounded-xl flex items-center justify-center shrink-0">
                  <Phone className="h-5 w-5 text-cyan-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Telefon</p>
                  <a href="tel:+491637917044" className="text-base font-semibold text-slate-800 hover:text-cyan-700 transition-colors">
                    0163 791 7044
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4" data-testid="contact-email">
                <div className="h-11 w-11 bg-green-50 rounded-xl flex items-center justify-center shrink-0">
                  <Mail className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">E-Mail</p>
                  <a href="mailto:lehnenundweinacht@gmail.com" className="text-base font-semibold text-slate-800 hover:text-cyan-700 transition-colors">
                    lehnenundweinacht@gmail.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4" data-testid="contact-location">
                <div className="h-11 w-11 bg-cyan-50 rounded-xl flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5 text-cyan-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Adresse</p>
                  <p className="text-base font-semibold text-slate-800">Hochstr. 72, 47929 Grefrath</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form side */}
          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div>
                <Label htmlFor="name" className="text-sm font-semibold text-slate-700 mb-1.5 block">
                  Name *
                </Label>
                <Input
                  id="name"
                  data-testid="contact-name-input"
                  placeholder="Ihr vollständiger Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-12 rounded-xl border-slate-200 bg-white text-base focus:ring-2 focus:ring-cyan-600 focus:ring-offset-2"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700 mb-1.5 block">
                  E-Mail *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email-input"
                  placeholder="ihre@email.de"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-12 rounded-xl border-slate-200 bg-white text-base focus:ring-2 focus:ring-cyan-600 focus:ring-offset-2"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-semibold text-slate-700 mb-1.5 block">
                  Telefon (optional)
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="contact-phone-input"
                  placeholder="0176 ..."
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="h-12 rounded-xl border-slate-200 bg-white text-base focus:ring-2 focus:ring-cyan-600 focus:ring-offset-2"
                />
              </div>
              <div>
                <Label htmlFor="message" className="text-sm font-semibold text-slate-700 mb-1.5 block">
                  Nachricht *
                </Label>
                <textarea
                  id="message"
                  data-testid="contact-message-input"
                  placeholder="Wie können wir Ihnen helfen?"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  className="flex w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-base shadow-sm placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 focus-visible:ring-offset-2 resize-none"
                />
              </div>
              <Button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={sending}
                className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 text-base font-semibold shadow-sm hover:shadow-md transition-all"
              >
                {sending ? (
                  "Wird gesendet..."
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Nachricht senden
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
